/*
 * GSM 06.10 Decoder for JavaScript
 *
 * Original code by Christopher Edwards
 * Ported to Javascript by Octavian Iosif (2025)
 *
 * This library is free software; you can redistribute it and/or
 * modify it under the terms of the GNU Library General Public
 * License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * This library is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU
 * Library General Public License for more details.
 *
 * You should have received a copy of the GNU Library General Public
 * License along with this library; if not, write to the Free
 * Software Foundation, Inc., 675 Mass Ave, Cambridge, MA 02139, USA.
 *
 * This software is a JavaScript port of the GSM Library provided by
 * Jutta Degener (jutta@cs.tu-berlin.de) and
 * Carsten Bormann (cabo@cs.tu-berlin.de),
 * Technische Universitaet Berlin
 */

import { InvalidGSMFrameException } from "./InvalidGSMFrameException.js";

export class GSMDecoder {
  constructor() {
    this.GSM_MAGIC = 0x0d;

    this.FAC = [18431, 20479, 22527, 24575, 26623, 28671, 30719, 32767];

    this.QLB = [3277, 11469, 21299, 32767];

    this.MIN_WORD = -32767 - 1;
    this.MAX_WORD = 32767;

    this.dp0 = new Array(280).fill(0);

    this.u = new Array(8).fill(0);
    this.LARpp = [new Array(8).fill(0), new Array(8).fill(0)];
    this.j = 0;

    this.nrp = 40;
    this.v = new Array(9).fill(0);
    this.msr = 0;

    // Working arrays
    this.LARc = new Array(8);
    this.Nc = new Array(4);
    this.Mc = new Array(4);
    this.bc = new Array(4);
    this.xmaxc = new Array(4);
    this.xmc = new Array(13 * 4);

    this.erp = new Array(40);
    this.wt = new Array(160);

    this.xMp = new Array(13);

    this.result = new Array(2);

    this.LARp = new Array(8);

    this.s = new Array(160);
  }

  /**
   * Decode GSM frame to PCM samples
   * @param {Uint8Array} frame - 33-byte GSM frame
   * @returns {Int16Array} - 160 PCM samples
   */
  decode(frame) {
    if (frame.length !== 33) {
      throw new InvalidGSMFrameException();
    }

    let i = 0;

    if (((frame[i] >> 4) & 0xf) !== this.GSM_MAGIC) {
      throw new InvalidGSMFrameException();
    }

    // Unpack the frame
    this.LARc[0] = (frame[i++] & 0xf) << 2;
    this.LARc[0] |= (frame[i] >> 6) & 0x3;
    this.LARc[1] = frame[i++] & 0x3f;
    this.LARc[2] = (frame[i] >> 3) & 0x1f;
    this.LARc[3] = (frame[i++] & 0x7) << 2;
    this.LARc[3] |= (frame[i] >> 6) & 0x3;
    this.LARc[4] = (frame[i] >> 2) & 0xf;
    this.LARc[5] = (frame[i++] & 0x3) << 2;
    this.LARc[5] |= (frame[i] >> 6) & 0x3;
    this.LARc[6] = (frame[i] >> 3) & 0x7;
    this.LARc[7] = frame[i++] & 0x7;

    this.Nc[0] = (frame[i] >> 1) & 0x7f;
    this.bc[0] = (frame[i++] & 0x1) << 1;
    this.bc[0] |= (frame[i] >> 7) & 0x1;
    this.Mc[0] = (frame[i] >> 5) & 0x3;
    this.xmaxc[0] = (frame[i++] & 0x1f) << 1;
    this.xmaxc[0] |= (frame[i] >> 7) & 0x1;

    this.xmc[0] = (frame[i] >> 4) & 0x7;
    this.xmc[1] = (frame[i] >> 1) & 0x7;
    this.xmc[2] = (frame[i++] & 0x1) << 2;
    this.xmc[2] |= (frame[i] >> 6) & 0x3;
    this.xmc[3] = (frame[i] >> 3) & 0x7;
    this.xmc[4] = frame[i++] & 0x7;
    this.xmc[5] = (frame[i] >> 5) & 0x7;
    this.xmc[6] = (frame[i] >> 2) & 0x7;
    this.xmc[7] = (frame[i++] & 0x3) << 1;
    this.xmc[7] |= (frame[i] >> 7) & 0x1;
    this.xmc[8] = (frame[i] >> 4) & 0x7;
    this.xmc[9] = (frame[i] >> 1) & 0x7;
    this.xmc[10] = (frame[i++] & 0x1) << 2;
    this.xmc[10] |= (frame[i] >> 6) & 0x3;
    this.xmc[11] = (frame[i] >> 3) & 0x7;
    this.xmc[12] = frame[i++] & 0x7;

    this.Nc[1] = (frame[i] >> 1) & 0x7f;
    this.bc[1] = (frame[i++] & 0x1) << 1;
    this.bc[1] |= (frame[i] >> 7) & 0x1;
    this.Mc[1] = (frame[i] >> 5) & 0x3;
    this.xmaxc[1] = (frame[i++] & 0x1f) << 1;
    this.xmaxc[1] |= (frame[i] >> 7) & 0x1;

    this.xmc[13] = (frame[i] >> 4) & 0x7;
    this.xmc[14] = (frame[i] >> 1) & 0x7;
    this.xmc[15] = (frame[i++] & 0x1) << 2;
    this.xmc[15] |= (frame[i] >> 6) & 0x3;
    this.xmc[16] = (frame[i] >> 3) & 0x7;
    this.xmc[17] = frame[i++] & 0x7;
    this.xmc[18] = (frame[i] >> 5) & 0x7;
    this.xmc[19] = (frame[i] >> 2) & 0x7;
    this.xmc[20] = (frame[i++] & 0x3) << 1;
    this.xmc[20] |= (frame[i] >> 7) & 0x1;
    this.xmc[21] = (frame[i] >> 4) & 0x7;
    this.xmc[22] = (frame[i] >> 1) & 0x7;
    this.xmc[23] = (frame[i++] & 0x1) << 2;
    this.xmc[23] |= (frame[i] >> 6) & 0x3;
    this.xmc[24] = (frame[i] >> 3) & 0x7;
    this.xmc[25] = frame[i++] & 0x7;

    this.Nc[2] = (frame[i] >> 1) & 0x7f;
    this.bc[2] = (frame[i++] & 0x1) << 1;
    this.bc[2] |= (frame[i] >> 7) & 0x1;
    this.Mc[2] = (frame[i] >> 5) & 0x3;
    this.xmaxc[2] = (frame[i++] & 0x1f) << 1;
    this.xmaxc[2] |= (frame[i] >> 7) & 0x1;

    this.xmc[26] = (frame[i] >> 4) & 0x7;
    this.xmc[27] = (frame[i] >> 1) & 0x7;
    this.xmc[28] = (frame[i++] & 0x1) << 2;
    this.xmc[28] |= (frame[i] >> 6) & 0x3;
    this.xmc[29] = (frame[i] >> 3) & 0x7;
    this.xmc[30] = frame[i++] & 0x7;
    this.xmc[31] = (frame[i] >> 5) & 0x7;
    this.xmc[32] = (frame[i] >> 2) & 0x7;
    this.xmc[33] = (frame[i++] & 0x3) << 1;
    this.xmc[33] |= (frame[i] >> 7) & 0x1;
    this.xmc[34] = (frame[i] >> 4) & 0x7;
    this.xmc[35] = (frame[i] >> 1) & 0x7;
    this.xmc[36] = (frame[i++] & 0x1) << 2;
    this.xmc[36] |= (frame[i] >> 6) & 0x3;
    this.xmc[37] = (frame[i] >> 3) & 0x7;
    this.xmc[38] = frame[i++] & 0x7;

    this.Nc[3] = (frame[i] >> 1) & 0x7f;
    this.bc[3] = (frame[i++] & 0x1) << 1;
    this.bc[3] |= (frame[i] >> 7) & 0x1;
    this.Mc[3] = (frame[i] >> 5) & 0x3;
    this.xmaxc[3] = (frame[i++] & 0x1f) << 1;
    this.xmaxc[3] |= (frame[i] >> 7) & 0x1;

    this.xmc[39] = (frame[i] >> 4) & 0x7;
    this.xmc[40] = (frame[i] >> 1) & 0x7;
    this.xmc[41] = (frame[i++] & 0x1) << 2;
    this.xmc[41] |= (frame[i] >> 6) & 0x3;
    this.xmc[42] = (frame[i] >> 3) & 0x7;
    this.xmc[43] = frame[i++] & 0x7;
    this.xmc[44] = (frame[i] >> 5) & 0x7;
    this.xmc[45] = (frame[i] >> 2) & 0x7;
    this.xmc[46] = (frame[i++] & 0x3) << 1;
    this.xmc[46] |= (frame[i] >> 7) & 0x1;
    this.xmc[47] = (frame[i] >> 4) & 0x7;
    this.xmc[48] = (frame[i] >> 1) & 0x7;
    this.xmc[49] = (frame[i++] & 0x1) << 2;
    this.xmc[49] |= (frame[i] >> 6) & 0x3;
    this.xmc[50] = (frame[i] >> 3) & 0x7;
    this.xmc[51] = frame[i] & 0x7;

    return this.decoder(
      this.LARc,
      this.Nc,
      this.bc,
      this.Mc,
      this.xmaxc,
      this.xmc
    );
  }

  decoder(LARcr, Ncr, bcr, Mcr, xmaxcr, xMcr) {
    for (let j = 0; j < 4; j++) {
      this.RPEDecoding(xmaxcr[j], Mcr[j], xMcr, j * 13, this.erp);
      this.longTermSynthesisFiltering(Ncr[j], bcr[j], this.erp, this.dp0);

      for (let k = 0; k < 40; k++) {
        this.wt[j * 40 + k] = this.dp0[120 + k];
      }
    }

    const s = this.shortTermSynthesisFilter(LARcr, this.wt);
    this.postprocessing(s);

    return new Int16Array(s);
  }

  RPEDecoding(xmaxcr, Mcr, xMcr, xMcrOffset, erp) {
    const expAndMant = this.xmaxcToExpAndMant(xmaxcr);
    this.APCMInverseQuantization(
      xMcr,
      xMcrOffset,
      expAndMant[0],
      expAndMant[1],
      this.xMp
    );
    this.RPE_grid_positioning(Mcr, this.xMp, erp);
  }

  xmaxcToExpAndMant(xmaxc) {
    let exp, mant;

    exp = 0;
    if (xmaxc > 15) {
      exp = (xmaxc >> 3) - 1;
    }
    mant = xmaxc - (exp << 3);

    if (mant === 0) {
      exp = -4;
      mant = 7;
    } else {
      while (mant <= 7) {
        mant = (mant << 1) | 1;
        exp--;
      }
      mant -= 8;
    }

    this.result[0] = exp;
    this.result[1] = mant;

    return this.result;
  }

  APCMInverseQuantization(xMc, xMcOffset, exp, mant, xMp) {
    const temp1 = this.FAC[mant];
    const temp2 = this.sub(6, exp);
    const temp3 = this.asl(1, this.sub(temp2, 1));

    let p = 0;

    for (let i = 13; i-- > 0; ) {
      let temp = (xMc[xMcOffset++] << 1) - 7;
      temp = temp << 12;
      temp = this.mult_r(temp1, temp);
      temp = this.add(temp, temp3);
      xMp[p++] = this.asr(temp, temp2);
    }
  }

  saturate(x) {
    return x < this.MIN_WORD
      ? this.MIN_WORD
      : x > this.MAX_WORD
      ? this.MAX_WORD
      : x;
  }

  sub(a, b) {
    const diff = a - b;
    return this.saturate(diff);
  }

  add(a, b) {
    const sum = a + b;
    return this.saturate(sum);
  }

  asl(a, n) {
    if (n >= 16) return 0;
    if (n <= -16) return a < 0 ? -1 : 0;
    if (n < 0) return this.asr(a, -n);
    return a << n;
  }

  asr(a, n) {
    if (n >= 16) return a < 0 ? -1 : 0;
    if (n <= -16) return 0;
    if (n < 0) return a << -n;
    return a >> n;
  }

  mult_r(a, b) {
    if (b === this.MIN_WORD && a === this.MIN_WORD) return this.MAX_WORD;
    else {
      const prod = a * b + 16384;
      return this.saturate(prod >> 15);
    }
  }

  longTermSynthesisFiltering(Ncr, bcr, erp, dp0) {
    let brp, drpp, Nr;

    Nr = Ncr < 40 || Ncr > 120 ? this.nrp : Ncr;
    this.nrp = Nr;

    brp = this.QLB[bcr];

    for (let k = 0; k <= 39; k++) {
      drpp = this.mult_r(brp, dp0[120 + (k - Nr)]);
      dp0[120 + k] = this.add(erp[k], drpp);
    }

    for (let k = 0; k <= 119; k++) {
      dp0[k] = dp0[40 + k];
    }
  }

  shortTermSynthesisFilter(LARcr, wt) {
    const LARpp_j = this.LARpp[this.j];
    const LARpp_j_1 = this.LARpp[(this.j ^= 1)];

    this.decodingOfTheCodedLogAreaRatios(LARcr, LARpp_j);

    this.Coefficients_0_12(LARpp_j_1, LARpp_j, this.LARp);
    this.LARp_to_rp(this.LARp);
    this.shortTermSynthesisFiltering(this.LARp, 13, wt, this.s, 0);

    this.Coefficients_13_26(LARpp_j_1, LARpp_j, this.LARp);
    this.LARp_to_rp(this.LARp);
    this.shortTermSynthesisFiltering(this.LARp, 14, wt, this.s, 13);

    this.Coefficients_27_39(LARpp_j_1, LARpp_j, this.LARp);
    this.LARp_to_rp(this.LARp);
    this.shortTermSynthesisFiltering(this.LARp, 13, wt, this.s, 27);

    this.Coefficients_40_159(LARpp_j, this.LARp);
    this.LARp_to_rp(this.LARp);
    this.shortTermSynthesisFiltering(this.LARp, 120, wt, this.s, 40);

    return this.s;
  }

  decodingOfTheCodedLogAreaRatios(LARc, LARpp) {
    let temp1;

    temp1 = this.add(LARc[0], -32) << 10;
    temp1 = this.mult_r(13107, temp1);
    LARpp[0] = this.add(temp1, temp1);

    temp1 = this.add(LARc[1], -32) << 10;
    temp1 = this.mult_r(13107, temp1);
    LARpp[1] = this.add(temp1, temp1);

    temp1 = this.add(LARc[2], -16) << 10;
    temp1 = this.sub(temp1, 4096);
    temp1 = this.mult_r(13107, temp1);
    LARpp[2] = this.add(temp1, temp1);

    temp1 = this.add(LARc[3], -16) << 10;
    temp1 = this.sub(temp1, -5120);
    temp1 = this.mult_r(13107, temp1);
    LARpp[3] = this.add(temp1, temp1);

    temp1 = this.add(LARc[4], -8) << 10;
    temp1 = this.sub(temp1, 188);
    temp1 = this.mult_r(19223, temp1);
    LARpp[4] = this.add(temp1, temp1);

    temp1 = this.add(LARc[5], -8) << 10;
    temp1 = this.sub(temp1, -3584);
    temp1 = this.mult_r(17476, temp1);
    LARpp[5] = this.add(temp1, temp1);

    temp1 = this.add(LARc[6], -4) << 10;
    temp1 = this.sub(temp1, -682);
    temp1 = this.mult_r(31454, temp1);
    LARpp[6] = this.add(temp1, temp1);

    temp1 = this.add(LARc[7], -4) << 10;
    temp1 = this.sub(temp1, -2288);
    temp1 = this.mult_r(29708, temp1);
    LARpp[7] = this.add(temp1, temp1);
  }

  Coefficients_0_12(LARpp_j_1, LARpp_j, LARp) {
    for (let i = 0; i < 8; i++) {
      LARp[i] = this.add(LARpp_j_1[i] >> 2, LARpp_j[i] >> 2);
      LARp[i] = this.add(LARp[i], LARpp_j_1[i] >> 1);
    }
  }

  Coefficients_13_26(LARpp_j_1, LARpp_j, LARp) {
    for (let i = 0; i < 8; i++) {
      LARp[i] = this.add(LARpp_j_1[i] >> 1, LARpp_j[i] >> 1);
    }
  }

  Coefficients_27_39(LARpp_j_1, LARpp_j, LARp) {
    for (let i = 0; i < 8; i++) {
      LARp[i] = this.add(LARpp_j_1[i] >> 2, LARpp_j[i] >> 2);
      LARp[i] = this.add(LARp[i], LARpp_j[i] >> 1);
    }
  }

  Coefficients_40_159(LARpp_j, LARp) {
    for (let i = 0; i < 8; i++) {
      LARp[i] = LARpp_j[i];
    }
  }

  LARp_to_rp(LARp) {
    let temp;

    for (let i = 0; i < 8; i++) {
      if (LARp[i] < 0) {
        temp = LARp[i] === this.MIN_WORD ? this.MAX_WORD : -LARp[i];
        LARp[i] = -(temp < 11059
          ? temp << 1
          : temp < 20070
          ? temp + 11059
          : this.add(temp >> 2, 26112));
      } else {
        temp = LARp[i];
        LARp[i] =
          temp < 11059
            ? temp << 1
            : temp < 20070
            ? temp + 11059
            : this.add(temp >> 2, 26112);
      }
    }
  }

  shortTermSynthesisFiltering(rrp, k, wt, sr, off) {
    let sri, tmp1, tmp2;
    let woff = off;
    let soff = off;

    while (k-- > 0) {
      sri = wt[woff++];
      for (let i = 8; i-- > 0; ) {
        tmp1 = rrp[i];
        tmp2 = this.v[i];
        tmp2 =
          tmp1 === this.MIN_WORD && tmp2 === this.MIN_WORD
            ? this.MAX_WORD
            : this.saturate((tmp1 * tmp2 + 16384) >> 15);
        sri = this.sub(sri, tmp2);

        tmp1 =
          tmp1 === this.MIN_WORD && sri === this.MIN_WORD
            ? this.MAX_WORD
            : this.saturate((tmp1 * sri + 16384) >> 15);
        this.v[i + 1] = this.add(this.v[i], tmp1);
      }
      sr[soff++] = this.v[0] = sri;
    }
  }

  postprocessing(s) {
    let soff = 0;
    let tmp;
    for (let k = 160; k-- > 0; soff++) {
      tmp = this.mult_r(this.msr, 28180);
      this.msr = this.add(s[soff], tmp);
      s[soff] = this.saturate(this.add(this.msr, this.msr) & ~0x7);
    }
  }

  RPE_grid_positioning(Mc, xMp, ep) {
    let i = 13;
    let epo = 0;
    let po = 0;

    switch (Mc) {
      case 3:
        ep[epo++] = 0;
        /* falls through */
      case 2:
        ep[epo++] = 0;
        /* falls through */
      case 1:
        ep[epo++] = 0;
        /* falls through */
      case 0:
        ep[epo++] = xMp[po++];
        i--;
        break;
      default:
        break;
    }

    do {
      ep[epo++] = 0;
      ep[epo++] = 0;
      ep[epo++] = xMp[po++];
    } while (--i > 0);

    while (++Mc < 4) {
      ep[epo++] = 0;
    }
  }
}
